/**
 * What goes IN the monthly report PDF and the verification certificate.
 *
 * KEEP THIS FILE IDENTICAL in the backend (src/utils/reportPdfContent.mjs) and
 * the frontend (src/utils/reportPdfContent.mjs), alongside pdfLayout.mjs. The
 * report is produced in three places - signed by the server for the approval
 * zip and the Download PDF button, by the browser if that call fails, and the
 * certificate on its own - and they used to be three hand-drawn documents that
 * had drifted apart (one printed "Fully Approved" on every report whatever its
 * status; only one showed each claim's integrity check).
 *
 * Pure: takes a layout from createPdfLayout and the report / certificate JSON,
 * draws, returns nothing.
 */
import { PDF_TONES } from './pdfLayout.mjs';

const money = (n) => `$${(Number(n) || 0).toLocaleString('en-US', {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
})}`;

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

export const reportMonthName = (m) => MONTHS[Number(m) - 1] || (m ? `Month ${m}` : '');

const titleCase = (v) => String(v || '-').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

/** What the report's status means, in words - not "Fully Approved" regardless. */
const REPORT_STATUS = {
  draft: 'Draft',
  submitted: 'With the auditor',
  auditor_approved: 'Audited',
  regional_director_approved: 'Approved by regional director',
  global_director_approved: 'Fully approved',
  rejected: 'Sent back',
};

const personName = (u) => (u ? `${u.firstName || ''} ${u.lastName || ''}`.trim() : '');

/** "Sep 20, 2026" from a date or timestamp, read in UTC so it cannot shift a day. */
const shortDate = (v) => {
  if (!v) return '-';
  const literal = String(v).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (literal) return `${MONTHS[Number(literal[2]) - 1].slice(0, 3)} ${literal[3]}, ${literal[1]}`;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return `${MONTHS[d.getUTCMonth()].slice(0, 3)} ${String(d.getUTCDate()).padStart(2, '0')}, ${d.getUTCFullYear()}`;
};

const claimTotalOf = (c) => Number(c?.total ?? c?.amount) || 0;

/** A claim's funding as parts: its splits, or its one code for the whole claim. */
const fundingParts = (c) => (Array.isArray(c?.splits) && c.splits.length > 0
  ? c.splits.map(s => ({ code: s.ptae || '', amount: Number(s.amount) || 0 }))
  : [{ code: c?.ptae || '', amount: claimTotalOf(c) }]);

// ------------------------------------------------------------------ headers
/** Title, subtitle and references for a monthly report's layout. */
export const monthlyReportHeader = (report) => {
  const period = `${reportMonthName(report?.month)} ${report?.year || ''}`.trim();
  return {
    title: 'MONTHLY EXPENSE REPORT',
    subtitle: [period, report?.region].filter(Boolean).join('  -  '),
    bandRef: [report?.region, period].filter(Boolean).join('  '),
    footerRef: report?.id ? `Report ${String(report.id)}` : '',
  };
};

/** Title, subtitle and references for a stand-alone certificate's layout. */
export const certificateHeader = (certificate) => {
  const r = certificate?.report || {};
  const period = `${r.monthName || reportMonthName(r.month)} ${r.year || ''}`.trim();
  return {
    title: 'DIGITAL VERIFICATION CERTIFICATE',
    subtitle: [period, r.region].filter(Boolean).join('  -  '),
    bandRef: [r.region, period].filter(Boolean).join('  '),
    footerRef: certificate?.document?.reportId ? `Report ${certificate.document.reportId}` : '',
  };
};

// ------------------------------------------------------------------ certificate
const drawCertificateBody = (layout, certificate, report) => {
  const C = PDF_TONES;
  const doc = certificate?.document || {};
  const r = certificate?.report || {};
  const chain = Array.isArray(certificate?.approvalChain) ? certificate.approvalChain : [];
  const claimChecks = Array.isArray(certificate?.claimVerification) ? certificate.claimVerification : [];
  const integrity = certificate?.integrityStatus || {};

  layout.info([
    ['Document ID', doc.reportId || report?.id || '-'],
    ['Region', r.region || report?.region || '-'],
    ['Period', `${r.monthName || reportMonthName(report?.month)} ${r.year || report?.year || ''}`.trim() || '-'],
    ['Total', money(r.totalAmount ?? report?.totalAmount)],
    ['Status', r.statusDisplay || REPORT_STATUS[report?.status] || titleCase(report?.status)],
    ['Version', `v${r.version ?? report?.version ?? 1}`],
    ['Claims', String(r.claimsCount ?? report?.claims?.length ?? '-')],
    ['Certificate issued', doc.generatedAtFormatted || '-'],
    ['Issued by', `${doc.generatedBy || 'Stanford Seed Expense Claim System'}${doc.version ? ` v${doc.version}` : ''}`],
  ]);

  layout.heading('APPROVAL CHAIN');
  layout.table(
    [
      { label: 'Step', w: 5, align: 'right' },
      { label: 'Stage', w: 17, wrap: 2 },
      { label: 'By', w: 17, wrap: 2 },
      { label: 'Date', w: 12, wrap: 2 },
      { label: 'Hash', w: 20, mono: true, wrap: 3 },
      { label: 'Previous hash', w: 20, mono: true, wrap: 3 },
      { label: 'Check', w: 9 },
    ],
    chain.map((e, i) => [
      String(e.step ?? i + 1),
      e.title || titleCase(e.stage),
      e.performedBy
        ? `${e.performedBy.name || '-'}${e.performedBy.roleDisplay ? `\n${e.performedBy.roleDisplay}` : ''}`
        : '-',
      e.timestampFormatted || shortDate(e.timestamp || e.performedAt),
      e.hash || '-',
      e.previousHash === 'GENESIS' ? 'GENESIS (chain start)' : (e.previousHash || '-'),
      e.verified
        ? { text: 'VERIFIED', colour: C.GREEN, bold: true }
        : { text: 'FAILED', colour: C.RED, bold: true },
    ]),
    { empty: 'No approval steps recorded.' }
  );

  if (claimChecks.length) {
    layout.heading('INDIVIDUAL CLAIM VERIFICATION');
    layout.table(
      [
        { label: 'Check', w: 12 },
        { label: 'Claimant', w: 34, wrap: 2 },
        { label: 'Type', w: 18 },
        { label: 'Amount', w: 18, align: 'right' },
        { label: 'Chain entries', w: 18, align: 'right' },
      ],
      claimChecks.map(cv => [
        cv.integrityValid
          ? { text: 'VERIFIED', colour: C.GREEN, bold: true }
          : { text: 'FAILED', colour: C.RED, bold: true },
        cv.employeeName || 'Unknown',
        cv.claimTypeDisplay || '-',
        money(cv.amount),
        String(cv.chainLength ?? '-'),
      ])
    );
  }

  const valid = integrity.valid !== false;
  layout.callout({
    tone: valid ? 'success' : 'danger',
    title: valid ? 'DOCUMENT INTEGRITY VERIFIED' : 'INTEGRITY COMPROMISED',
    lines: [
      valid ? 'All hashes verified - no tampering detected.' : 'WARNING: some hashes failed verification.',
      { text: 'Digital fingerprint', muted: true },
      { text: integrity.digitalFingerprint || 'Not generated', mono: true },
      { text: `Chain length: ${integrity.chainLength ?? chain.length}  |  Claims verified: ${integrity.claimsVerified ?? claimChecks.length}`, muted: true },
    ],
  });
};

/** A stand-alone verification certificate. */
export const drawVerificationCertificate = (layout, certificate) => {
  drawCertificateBody(layout, certificate, null);
};

// ------------------------------------------------------------------ report
/**
 * The signers' marks, embedded ahead of drawing - pdf-lib embeds images
 * asynchronously and drawMonthlyReport is synchronous. Keyed by stage hash.
 * An image that will not embed is left out and its stamp prints as text.
 *
 * @param {PDFDocument} pdfDoc
 * @param {object} certificate  from getVerificationCertificate
 * @returns {Promise<Map<string, PDFImage>>}
 */
export const embedSignatureImages = async (pdfDoc, certificate) => {
  const images = new Map();
  for (const e of (Array.isArray(certificate?.approvalChain) ? certificate.approvalChain : [])) {
    const url = e?.signature?.imageDataUrl;
    if (!url || !e.hash || images.has(e.hash)) continue;
    try {
      images.set(e.hash, await pdfDoc.embedPng(url));
    } catch {
      // Printed as text instead - a report is never lost to one bad image.
    }
  }
  return images;
};

/**
 * The monthly report: details, every claim with its funding and integrity
 * check, the totals by PTAE, the approval signatures - and, when a certificate
 * is given, the verification certificate on a page of its own.
 *
 * `signatureImages` is embedSignatureImages' result; without it every stamp
 * prints as text.
 */
export const drawMonthlyReport = (layout, report, certificate = null, { signatureImages = null } = {}) => {
  const C = PDF_TONES;
  const claims = Array.isArray(report?.claims) ? report.claims : [];
  const checks = new Map((certificate?.claimVerification || [])
    .map(cv => [String(cv.reportClaimId), cv]));
  const perDiemTotal = claims.reduce((t, c) => t + (Number(c.perDiem) || 0), 0);
  const expenseTotal = claims.reduce((t, c) => t + (Number(c.reimbursements) || 0), 0);
  const preparer = personName(report?.preparer || report?.preparedByUser);

  layout.info([
    ['Region', report?.region || '-'],
    ['Period', `${reportMonthName(report?.month)} ${report?.year || ''}`.trim() || '-'],
    ['Status', REPORT_STATUS[report?.status] || certificate?.report?.statusDisplay || titleCase(report?.status)],
    ['Version', `v${report?.version || 1}`],
    ['Prepared by', preparer || '-'],
    ['Claims', String(claims.length)],
    ['Per diem', money(perDiemTotal)],
    ['Expenses', money(expenseTotal)],
    ['Total', money(report?.totalAmount)],
    ['Final approval', report?.globalDirectorApprovedAt ? shortDate(report.globalDirectorApprovedAt) : 'Pending'],
  ]);

  // ---- claims
  const withChecks = checks.size > 0;
  const cols = [
    { label: '#', w: 3, align: 'right' },
    { label: 'Claimant', w: 17, wrap: 2 },
    { label: 'Type', w: 8 },
    { label: 'Per Diem', w: 10, align: 'right' },
    { label: 'Reimbursements', w: 11, align: 'right' },
    { label: 'Total', w: 10, align: 'right' },
    { label: 'PTAE', w: withChecks ? 30 : 41, wrap: 8 },
  ];
  if (withChecks) cols.push({ label: 'Integrity', w: 11 });

  layout.heading('CLAIMS');
  layout.table(
    cols,
    claims.map((c, i) => {
      const parts = fundingParts(c);
      const ptae = parts.length > 1
        ? parts.map(p => `${p.code || 'Not assigned'}  ${money(p.amount)}`).join('\n')
        : (parts[0].code || 'Not assigned');
      const row = [
        String(i + 1),
        c.userName || 'Unknown',
        c.claimType === 'ExpenseClaim' ? 'Expense' : 'Per Diem',
        money(c.perDiem),
        money(c.reimbursements),
        money(claimTotalOf(c)),
        ptae,
      ];
      if (withChecks) {
        const cv = checks.get(String(c.id));
        row.push(!cv ? '-'
          : cv.integrityValid
            ? { text: 'Verified', colour: C.GREEN, bold: true }
            : { text: 'Failed', colour: C.RED, bold: true });
      }
      return row;
    }),
    { footer: ['TOTAL (USD)', money(report?.totalAmount)], empty: 'No claims in this report.' }
  );

  // ---- funding by PTAE: what each code carries across the whole report
  const byCode = new Map();
  claims.forEach((c, i) => {
    for (const p of fundingParts(c)) {
      const key = p.code || '';
      if (!byCode.has(key)) byCode.set(key, { amount: 0, claims: new Set() });
      const b = byCode.get(key);
      b.amount += p.amount;
      b.claims.add(String(c.id ?? c.claimId ?? `row-${i}`));
    }
  });
  if (byCode.size) {
    const rows = [...byCode.entries()]
      .sort(([a], [b]) => (a === '' ? 1 : b === '' ? -1 : a.localeCompare(b)))
      .map(([code, b]) => [
        code || { text: 'Not assigned', colour: C.RED, bold: true },
        String(b.claims.size),
        money(b.amount),
      ]);
    const allocated = [...byCode.values()].reduce((t, b) => t + b.amount, 0);
    layout.heading('FUNDING BY PTAE');
    layout.table(
      [
        { label: 'PTAE', w: 60, wrap: 2 },
        { label: 'Claims', w: 14, align: 'right' },
        { label: 'Amount (USD)', w: 26, align: 'right' },
      ],
      rows,
      { width: layout.usableWidth * 0.66, footer: ['Allocated', money(allocated)] }
    );
  }

  // ---- approval signatures: one stamp per stage that has happened
  const chain = Array.isArray(certificate?.approvalChain) ? certificate.approvalChain : [];
  // The listed stages are in order of preference: the latest entry of the
  // first one that happened. A plain find in chain order picked the report's
  // 'draft' entry (written when it was generated, unsigned) ahead of the
  // signed 'submitted' one, so the admin's signature never printed.
  const entryFor = (stages) => {
    for (const stage of stages) {
      const found = chain.filter(e => e.stage === stage);
      if (found.length) return found[found.length - 1];
    }
    return null;
  };
  const signers = [
    {
      label: 'Prepared (Regional Admin)', reason: 'Prepared and submitted this report', verb: 'Submitted',
      entry: entryFor(['submitted', 'resubmitted', 'draft']),
      user: report?.preparer || report?.preparedByUser, date: report?.createdAt,
    },
    {
      label: 'Auditor', reason: 'Reviewed and approved this report',
      entry: entryFor(['auditor_approved']),
      user: report?.auditorApprover || report?.auditorApprovedByUser, date: report?.auditorApprovedAt,
    },
    {
      label: 'Regional Director', reason: 'Reviewed and approved this report',
      entry: entryFor(['regional_director_approved']),
      user: report?.regionalDirectorApprover || report?.regionalDirectorApprovedByUser,
      date: report?.regionalDirectorApprovedAt,
    },
    {
      label: 'Final approval (Finance / Global Director)', reason: 'Gave final approval for this report',
      entry: entryFor(['global_director_approved']),
      user: report?.globalDirectorApprover || report?.globalDirectorApprovedByUser,
      date: report?.globalDirectorApprovedAt,
    },
  ].filter(s => s.entry || s.user);

  // Signed stages (submit, regional, final - 2026-10-21) carry the signer's
  // mark. The auditor's stage, and anything approved before signatures
  // existed, prints the same stamp with text where the mark would be.
  // No IP address (2026-10-06): kept server-side as audit metadata only.
  // Room for the first row of stamps, so the heading is never stranded.
  layout.heading('APPROVAL SIGNATURES', 125);
  if (signers.length === 0) {
    layout.note('No approvals recorded yet.');
  } else {
    layout.signatureBlocks(signers.map(s => {
      const who = s.entry?.performedBy?.name || personName(s.user) || 'Unknown';
      const role = s.entry?.performedBy?.roleDisplay || '';
      const email = s.entry?.performedBy?.email || s.user?.email || '';
      const verb = s.verb || 'Approved';
      const sig = s.entry?.signature || null;
      const signed = !!sig && !sig.missing;
      const image = signed && s.entry?.hash ? (signatureImages?.get(s.entry.hash) || null) : null;
      return {
        label: s.label,
        image,
        fallback: sig?.missing ? 'SIGNATURE MISSING' : `${verb} electronically`,
        alert: !!sig?.missing,
        lines: [
          { text: `${signed ? 'Electronically signed by' : `${verb} by`} ${who}`, bold: true, wrap: 2 },
          { text: [role, email].filter(Boolean).join(', '), muted: true },
          { text: `Reason: ${s.reason}`, wrap: 2 },
          { text: `Date: ${sig?.signedAtFormatted || s.entry?.timestampFormatted || shortDate(s.date)}` },
          signed ? { text: `Signature: ${sig.methodDisplay || sig.method}`, muted: true } : null,
          s.entry?.takeover
            ? { text: `Took over final approval from ${s.entry.takeover.fromName || 'the named approver'}`, muted: true, wrap: 2 }
            : null,
          signed && sig.verified === false ? { text: 'SIGNATURE FAILED VERIFICATION', alert: true } : null,
        ],
        hash: s.entry?.hash ? `Stage hash ${s.entry.hash}` : '',
      };
    }));
  }
  layout.note('Each stage is stamped with the signer, the time and the hash of the report at that moment. '
    + 'A signature is checked against the fingerprint the approval chain recorded when it was made.');

  // ---- the certificate, on its own page
  if (certificate) {
    layout.newPage();
    layout.heading('DIGITAL VERIFICATION CERTIFICATE', 120);
    drawCertificateBody(layout, certificate, report);
  }
};

const reportPdfContent = {
  drawMonthlyReport, drawVerificationCertificate, monthlyReportHeader, certificateHeader, reportMonthName,
  embedSignatureImages,
};
export default reportPdfContent;
